import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronDown, Users, LogIn, LogOut, BookOpen,
  FileText, Video, Headphones, Presentation, Lock, CheckCircle2,
  MessageSquare, Upload, Clock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import PostComposer from './PostComposer';
import PostCard from './PostCard';
import { prepareImage } from '../lib/imageResize';
import AdSlot from './AdSlot';
import confetti from 'canvas-confetti';

const contentTypeIcons = {
  text: FileText,
  video: Video,
  audio: Headphones,
  slides: Presentation,
  image: FileText,
  pdf: FileText,
};

/* Lesson links open in a new tab; DOMPurify drops target/rel unless they are set after cleaning */
const sanitizeLesson = (html) => {
  const clean = DOMPurify.sanitize(html);
  const box = document.createElement('div');
  box.innerHTML = clean;
  box.querySelectorAll('a[href]').forEach((a) => { a.target = '_blank'; a.rel = 'noopener noreferrer'; });
  return box.innerHTML;
};

/* ─── Text block renderer (HTML from tiptap or legacy markdown) ─── */
const RenderText = ({ text }) => {
  if (!text) return null;
  const proseClasses = 'lesson-prose';
  if (text.trim().startsWith('<')) {
    return (
      <div className={proseClasses}
        dangerouslySetInnerHTML={{ __html: sanitizeLesson(text) }}
        data-testid="lesson-html-content" />
    );
  }
  return (
    <div className={proseClasses} data-testid="lesson-text-content">
      {text.split('\n').map((line, i) => {
        if (line.startsWith('## ')) return <h2 key={i}>{line.replace('## ', '')}</h2>;
        if (line.startsWith('### ')) return <h3 key={i}>{line.replace('### ', '')}</h3>;
        if (line.startsWith('**') && line.endsWith('**')) return <p key={i}><strong>{line.replace(/\*\*/g, '')}</strong></p>;
        if (line.startsWith('- **')) {
          const parts = line.replace('- **', '').split('**');
          return <li key={i}><strong>{parts[0]}</strong>{parts.slice(1).join('')}</li>;
        }
        if (line.startsWith('- ')) return <li key={i}>{line.replace('- ', '')}</li>;
        if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) return <p key={i}><em>{line.replace(/\*/g, '')}</em></p>;
        if (/^\d+\.\s/.test(line)) return <li key={i}>{line.replace(/^\d+\.\s/, '')}</li>;
        if (line.trim() === '') return <br key={i} />;
        return <p key={i}>{line}</p>;
      })}
    </div>
  );
};

/* Optional heading and short intro above a media block (set in the course builder) */
const MediaIntro = ({ title, description }) => {
  if (!title && !description) return null;
  return (
    <div className="space-y-1">
      {title && <h3 className="font-heading text-lg font-semibold text-text-main">{title}</h3>}
      {description && <p className="text-sm text-text-muted leading-relaxed whitespace-pre-line">{description}</p>}
    </div>
  );
};

/* ─── Multi-type lesson content renderer ─── */
const RenderContent = ({ lesson }) => {
  const { content_text, content_image_url, content_video_url, content_pdf_url,
          content_audio_url, content_slides_url, content_type, content_url, title } = lesson;

  // Backward compat: if new per-type columns are empty, fall back to legacy content_type + content_url
  const imageUrl  = content_image_url  || (content_type === 'image'  ? content_url : null);
  const videoUrl  = content_video_url  || (content_type === 'video'  ? content_url : null);
  const pdfUrl    = content_pdf_url    || (content_type === 'pdf'    ? content_url : null);
  const audioUrl  = content_audio_url  || (content_type === 'audio'  ? content_url : null);
  const slidesUrl = content_slides_url || (content_type === 'slides' ? content_url : null);

  const youtubeStart = (u) => {
    const raw = u.searchParams.get('start') || u.searchParams.get('t');
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return raw;
    const m = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
    return m ? String((+m[1] || 0) * 3600 + (+m[2] || 0) * 60 + (+m[3] || 0)) : null;
  };
  const toEmbedUrl = (url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      let id = null;
      if (u.hostname.includes('youtube.com') && u.pathname === '/watch') id = u.searchParams.get('v');
      else if (u.hostname === 'youtu.be') id = u.pathname.slice(1);
      if (!id) return url;
      const start = youtubeStart(u);
      return `https://www.youtube.com/embed/${id}${start ? `?start=${start}` : ''}`;
    } catch {
      return url;
    }
  };

  // A paragraph reading [[after-media]] splits the text: what follows shows below the video, slides and workbook.
  const [textTop, textBottom] = (content_text || '').split(/<p>\s*\[\[after-media\]\]\s*<\/p>/);

  const hasAnyContent = content_text || imageUrl || videoUrl || pdfUrl || audioUrl || slidesUrl;
  if (!hasAnyContent) return null;

  return (
    <div className="space-y-6" data-testid="lesson-content-blocks">
      {/* Audio */}
      {audioUrl && (
        <div className="p-6 bg-primary/5 rounded-xl" data-testid="lesson-audio">
          <MediaIntro title={lesson.content_audio_title} description={lesson.content_audio_description} />
          <audio controls className="w-full mt-3" src={audioUrl}>Your browser does not support audio.</audio>
        </div>
      )}

      {/* Text */}
      {textTop && <RenderText text={textTop} />}

      {/* Image */}
      {imageUrl && (
        <div className="rounded-xl overflow-hidden" data-testid="lesson-image">
          <img src={imageUrl} alt={title} className="w-full max-h-[500px] object-contain bg-primary/5 rounded-xl" />
        </div>
      )}

      {/* YouTube video */}
      {videoUrl && (
        <div className="space-y-3" data-testid="lesson-video">
          <MediaIntro title={lesson.content_video_title} description={lesson.content_video_description} />
          <div className="aspect-video rounded-xl overflow-hidden bg-black">
            <iframe src={toEmbedUrl(videoUrl)} className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen title={lesson.content_video_title || title} />
          </div>
        </div>
      )}

      {/* Slides */}
      {slidesUrl && (
        <div className="space-y-3" data-testid="lesson-slides">
          <MediaIntro title={lesson.content_slides_title} description={lesson.content_slides_description} />
          <div className="aspect-video rounded-xl overflow-hidden border border-primary/10">
            <iframe src={slidesUrl} className="w-full h-full" allowFullScreen title={lesson.content_slides_title || title || 'Slides'}
              style={{ border: 'none' }} />
          </div>
        </div>
      )}
      {/* PDF */}
      {pdfUrl && (
        <div className="space-y-3" data-testid="lesson-pdf">
          <MediaIntro title={lesson.content_pdf_title} description={lesson.content_pdf_description} />
          <div className="w-full rounded-xl overflow-hidden border border-primary/10" style={{ height: '600px' }}>
            <iframe src={pdfUrl} className="w-full h-full" title={lesson.content_pdf_title || title || 'Document'} />
          </div>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium">
            <FileText className="w-4 h-4" aria-hidden="true" /> Open in a new tab to fill in, save or print
          </a>
        </div>
      )}

      {textBottom && <RenderText text={textBottom} />}

    </div>
  );
};

/* ─── Course Outline Sidebar (read-only for all users) ─── */
const CourseOutline = ({ modules, activeLesson, onSelectLesson, isMember }) => {
  const [expandedModules, setExpandedModules] = useState({});

  useEffect(() => {
    if (activeLesson) {
      for (const m of modules) {
        if ((m.lessons || []).find(l => l.id === activeLesson)) {
          setExpandedModules(prev => ({ ...prev, [m.id]: true }));
          break;
        }
      }
    }
  }, [activeLesson, modules]);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  return (
    <div className="space-y-1" data-testid="course-outline">
      {modules.map((module) => {
        const isExpanded = expandedModules[module.id];
        const lessons = module.lessons || [];
        const completedCount = module.lessons_completed || 0;
        const totalCount = module.lessons_total || lessons.length;

        return (
          <div key={module.id} className="rounded-xl overflow-hidden" data-testid={`module-${module.id}`}>
            {/* Module row: icon, title, count — all horizontally aligned */}
            <button onClick={() => toggleModule(module.id)}
              className="w-full grid grid-cols-[2rem_1fr_auto_1.5rem] items-center gap-2.5 p-3 hover:bg-primary/5 transition-colors text-left"
              data-testid={`module-toggle-${module.id}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                module.completed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-primary/10'
              }`}>
                {module.completed ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <BookOpen className="w-4 h-4 text-primary" />}
              </div>
              <p className="text-base font-medium text-text-main truncate">{module.title}</p>
              <span className="text-base text-text-muted whitespace-nowrap">{completedCount}/{totalCount}</span>
              <ChevronDown className={`w-4 h-4 text-text-muted transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {/* Progress bar */}
            {totalCount > 0 && (
              <div className="px-3 pb-1">
                <div className="h-1 bg-primary/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round((completedCount / totalCount) * 100)}%` }} />
                </div>
              </div>
            )}

            <AnimatePresence>
              {isExpanded && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                  <div className="pl-4 pb-2 space-y-0.5">
                    {lessons.map((lesson) => {
                      const Icon = contentTypeIcons[lesson.content_type] || FileText;
                      const isActive = activeLesson === lesson.id;
                      const isLocked = !lesson.unlocked;
                      const canClick = isMember && !isLocked;

                      if (lesson.coming_soon) {
                        return (
                          <div key={lesson.id} className="w-full flex items-center gap-2.5 p-2.5 rounded-lg opacity-60"
                            data-testid={`lesson-${lesson.id}`}>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-primary/5">
                              <Clock className="w-3 h-3 text-primary/50" aria-hidden="true" />
                            </div>
                            <span className="text-sm flex-1 min-w-0 truncate text-text-muted">{lesson.title}</span>
                            <span className="text-[11px] font-medium text-text-muted bg-primary/10 rounded-full px-2 py-0.5 whitespace-nowrap">Coming soon</span>
                          </div>
                        );
                      }

                      return (
                        <button key={lesson.id} onClick={() => canClick && onSelectLesson(lesson.id)}
                          disabled={!canClick}
                          className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-all ${
                            isActive ? 'bg-primary/10 border-l-2 border-primary' :
                            isLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/5'
                          } ${!isMember ? 'opacity-40 cursor-not-allowed' : ''}`}
                          data-testid={`lesson-${lesson.id}`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                            lesson.completed ? 'bg-green-100 dark:bg-green-900/30' :
                            isLocked ? 'bg-primary/5' : 'bg-primary/10'
                          }`}>
                            {lesson.completed ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> :
                             isLocked ? <Lock className="w-3 h-3 text-primary/40" /> :
                             <Icon className="w-3 h-3 text-primary" />}
                          </div>
                          <span className={`text-sm flex-1 min-w-0 truncate ${
                            isActive ? 'font-medium text-primary' :
                            lesson.completed ? 'text-green-600 dark:text-green-400' :
                            isLocked ? 'text-text-muted' : 'text-text-main'
                          }`}>{lesson.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Lesson Viewer (read-only, with Mark Complete) ─── */
const LessonViewer = ({ lessonId, userId, onComplete, onBack }) => {
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (!lessonId) return;
    setLoading(true);
    setIsCompleted(false);
    api.getLesson(lessonId, userId).then(r => {
      setLesson(r.data);
      setIsCompleted(r.data?.completed === true);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [lessonId, userId]);

  const handleMarkComplete = async () => {
    if (isCompleted || completing) return;
    setCompleting(true);
    try {
      const res = await api.completeLesson(lessonId, userId);
      if (res.data?.completed) {
        setIsCompleted(true);
        confetti({ particleCount: 60, spread: 60, origin: { y: 0.7 }, colors: ['#4AADA8', '#A99CC8', '#FFD700'] });
        onComplete?.(res.data);
      } else if (res.data?.already_completed) {
        setIsCompleted(true);
      }
    } catch (e) { console.error(e); }
    finally { setCompleting(false); }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 p-6">
        <div className="h-8 w-2/3 bg-primary/10 rounded" />
        <div className="h-4 w-1/3 bg-primary/5 rounded" />
        <div className="h-64 bg-primary/5 rounded-xl" />
      </div>
    );
  }

  if (!lesson) return <div className="text-center py-12 text-text-muted">Lesson not found.</div>;

  return (
    <div className="p-6" data-testid="lesson-viewer">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-1.5 hover:bg-primary/10 rounded-full" data-testid="lesson-back-btn">
          <ChevronLeft className="w-5 h-5 text-primary" />
        </button>
        <div>
          <h2 className="font-heading text-lg font-semibold text-text-main" data-testid="lesson-title">{lesson.title}</h2>
          {lesson.description && <p className="text-xs text-text-muted mt-0.5">{lesson.description}</p>}
        </div>
      </div>

      <div className="glass-card rounded-xl p-6 mb-6" data-testid="lesson-content">
        <RenderContent lesson={lesson} />
      </div>

      <div className="flex justify-end">
        {isCompleted ? (
          <div className="flex items-center gap-2 px-6 py-3 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-sm font-medium"
            data-testid="lesson-completed-badge">
            <CheckCircle2 className="w-5 h-5" /> Lesson Complete
          </div>
        ) : (
          <button onClick={handleMarkComplete} disabled={completing}
            className="px-6 py-3 bg-primary text-white rounded-full text-sm font-medium btn-hover flex items-center gap-2 disabled:opacity-50"
            data-testid="mark-complete-btn">
            {completing ? 'Saving...' : <><CheckCircle2 className="w-4 h-4" /> Mark Complete</>}
          </button>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   SPACE PAGE
   ═══════════════════════════════════════════════ */
const SpacePage = ({ spaceId, onBack }) => {
  const { profile } = useAuth();
  const isAdmin = profile?.is_admin === true;
  const [space, setSpace] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [modules, setModules] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('lessons');
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [memberCount, setMemberCount] = useState(0);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [headerMessage, setHeaderMessage] = useState({ type: '', text: '' });

  const fetchData = useCallback(async () => {
    if (!spaceId) return;
    setLoading(true);
    try {
      const [spaceRes, modulesRes] = await Promise.all([
        api.getGroup(spaceId),
        api.getSpaceModules(spaceId, profile?.id),
      ]);
      setSpace(spaceRes.data);
      setModules(modulesRes.data || []);
      setMemberCount(spaceRes.data?.member_count || 0);

      if (profile?.id) {
        const memberRes = await api.checkMembership(spaceId, profile.id);
        setIsMember(memberRes.data?.joined || false);
      }
      const postsRes = await api.getSpacePosts(spaceId, { viewer_id: profile?.id });
      setPosts(postsRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [spaceId, profile?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // A member who lands here directly (typed URL, old bookmark) goes back to the feed
  useEffect(() => {
    if (!loading && space?.is_coming_soon && !isAdmin) onBack();
  }, [loading, space, isAdmin, onBack]);

  const refreshModules = async () => {
    const res = await api.getSpaceModules(spaceId, profile?.id);
    setModules(res.data || []);
  };

  const handleJoin = async () => {
    if (!profile?.id) return;
    await api.joinSpace(spaceId, profile.id);
    setIsMember(true);
    setMemberCount(c => c + 1);
    refreshModules();
  };

  const handleHeaderUpload = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setUploadingHeader(true);
    setHeaderMessage({ type: '', text: '' });
    try {
      const { file, note } = await prepareImage(picked, { maxDimension: 2000 });
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.uploadSpaceHeader(spaceId, formData);
      if (res.data?.url) {
        setSpace(prev => ({ ...prev, header_image: res.data.url }));
      }
      setHeaderMessage({ type: 'success', text: note || 'Header photo updated.' });
    } catch (err) {
      setHeaderMessage({ type: 'error', text: err?.response?.data?.detail || err?.message || 'Upload failed. Please try another photo.' });
    }
    setUploadingHeader(false);
    e.target.value = '';
  };

  const handleLeave = async () => {
    if (!profile?.id) return;
    await api.leaveSpace(spaceId, profile.id);
    setIsMember(false);
    setMemberCount(c => Math.max(0, c - 1));
  };

  const handleLessonComplete = () => refreshModules();
  const handlePostCreated = () => api.getSpacePosts(spaceId, { viewer_id: profile?.id }).then(r => setPosts(r.data || []));

  const totalLessons = modules.reduce((sum, m) => sum + (m.lessons_total || 0), 0);
  const completedLessons = modules.reduce((sum, m) => sum + (m.lessons_completed || 0), 0);

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto p-4 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-primary/5 rounded-xl" />
          <div className="h-8 w-48 bg-primary/10 rounded" />
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="max-w-[1200px] mx-auto p-4 lg:p-8 text-center">
        <p className="text-text-muted">Space not found.</p>
        <button onClick={onBack} className="mt-4 text-primary hover:underline">Go back</button>
      </div>
    );
  }

  // Coming-soon spaces are closed to members; the redirect effect above sends them back
  if (space.is_coming_soon && !isAdmin) return null;

  return (
    <div className="max-w-[1200px] mx-auto p-4 lg:p-8 pb-24">
      {/* Header */}
      <div className="mb-6">
        {/* Header image */}
        <div className="relative h-40 rounded-xl overflow-hidden mb-4 bg-primary/5">
          {space.header_image ? (
            <img src={space.header_image} alt={space.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BookOpen className="w-12 h-12 text-primary/20" aria-hidden="true" />
            </div>
          )}
          {isAdmin && (
            <label
              className="absolute bottom-3 right-3 min-h-[44px] flex items-center gap-2 px-4 bg-white/90 text-text-main rounded-full text-sm font-medium cursor-pointer hover:bg-white focus-within:ring-2 focus-within:ring-primary"
              data-testid="upload-header-overlay"
            >
              <Upload className="w-4 h-4" aria-hidden="true" />
              {uploadingHeader ? 'Uploading...' : 'Change Header'}
              <input
                type="file"
                accept="image/*,.heic,.heif"
                onChange={handleHeaderUpload}
                disabled={uploadingHeader}
                className="sr-only"
              />
            </label>
          )}
        </div>
        {isAdmin && (
          <div className="-mt-2 mb-3 text-xs" aria-live="polite">
            {headerMessage.text
              ? <p role={headerMessage.type === 'error' ? 'alert' : 'status'} className={headerMessage.type === 'error' ? 'text-red-600 text-sm' : 'text-green-700 text-sm'} data-testid="header-photo-message">{headerMessage.text}</p>
              : <p className="text-text-muted">Header photo: wide images work best (about 2000 x 300 px). Any photo is resized for you.</p>}
          </div>
        )}

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-primary/10 rounded-full" data-testid="space-back-btn">
              <ChevronLeft className="w-6 h-6 text-primary" />
            </button>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-primary" data-testid="space-title">{space.name}</h1>
              <div className="flex items-center gap-3 mt-1 text-xs text-text-muted">
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{memberCount} members</span>
                {totalLessons > 0 && <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" />{totalLessons} lessons</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isMember && totalLessons > 0 && (
              <div className="text-right">
                <p className="text-xs text-text-muted">{completedLessons}/{totalLessons} lessons</p>
                <div className="w-24 h-1.5 bg-primary/10 rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round((completedLessons / totalLessons) * 100)}%` }} />
                </div>
              </div>
            )}
            {isMember ? (
              <button onClick={handleLeave}
                className="px-4 py-2 border border-primary/30 text-primary rounded-full text-sm font-medium hover:bg-primary/5 flex items-center gap-2"
                data-testid="leave-space-btn"><LogOut className="w-4 h-4" /> Leave</button>
            ) : (
              <button onClick={handleJoin}
                className="px-5 py-2 bg-primary text-white rounded-full text-sm font-medium btn-hover flex items-center gap-2"
                data-testid="join-space-btn"><LogIn className="w-4 h-4" /> Join Space</button>
            )}
          </div>
        </div>

        {space.description && (
          <p className="text-sm text-text-muted mt-3 ml-14">{space.description}</p>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-6 p-1 bg-primary/5 rounded-full w-fit" data-testid="space-tabs">
        <button onClick={() => { setActiveTab('lessons'); setActiveLessonId(null); }}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'lessons' ? 'bg-primary text-white' : 'text-text-muted hover:text-text-main'}`}
          data-testid="tab-lessons"><BookOpen className="w-4 h-4 inline mr-1.5" />Lessons</button>
        <button onClick={() => setActiveTab('feed')}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'feed' ? 'bg-primary text-white' : 'text-text-muted hover:text-text-main'}`}
          data-testid="tab-feed"><MessageSquare className="w-4 h-4 inline mr-1.5" />Feed</button>
      </div>

      {/* Content */}
      {activeTab === 'lessons' ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <div className="glass-card rounded-xl p-4 sticky top-24">
              <h3 className="text-sm font-medium text-text-main mb-3 px-1 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary" /> Course Outline
              </h3>
              {modules.length === 0 ? (
                <p className="text-xs text-text-muted p-3">No modules available yet.</p>
              ) : (
                <CourseOutline
                  modules={modules} activeLesson={activeLessonId}
                  onSelectLesson={(id) => setActiveLessonId(id)}
                  isMember={isMember}
                />
              )}
              {!isMember && !isAdmin && modules.length > 0 && (
                <div className="mt-3 p-3 bg-primary/5 rounded-xl text-center">
                  <p className="text-[11px] text-text-muted mb-2">Join this space to access lessons</p>
                  <button onClick={handleJoin}
                    className="px-4 py-1.5 bg-primary text-white rounded-full text-xs font-medium btn-hover"
                    data-testid="outline-join-btn">Join Space</button>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-8">
            {activeLessonId ? (
              <div className="glass-card rounded-xl overflow-hidden">
                <LessonViewer
                  lessonId={activeLessonId} userId={profile?.id}
                  onComplete={handleLessonComplete} onBack={() => setActiveLessonId(null)}
                />
              </div>
            ) : (
              <div className="glass-card rounded-xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-medium text-text-main mb-2">
                  {isMember || isAdmin ? 'Select a lesson to begin' : 'Join this space to start learning'}
                </h3>
                <p className="text-sm text-text-muted max-w-md mx-auto">
                  {isMember || isAdmin
                    ? 'Choose a lesson from the course outline on the left. Complete lessons in order to unlock the next one.'
                    : `${space.name} has ${totalLessons} lessons across ${modules.length} modules. Join to start your learning journey.`}
                </p>
                {!isMember && !isAdmin && (
                  <button onClick={handleJoin}
                    className="mt-4 px-6 py-2.5 bg-primary text-white rounded-full text-sm font-medium btn-hover"
                    data-testid="welcome-join-btn">Join Space</button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_18rem] gap-6">
          <div className="space-y-6">
            {isMember && <PostComposer onPostCreated={handlePostCreated} spaceId={spaceId} />}
            {posts.length === 0 ? (
              <div className="glass-card rounded-xl p-8 text-center">
                <p className="text-text-muted">No posts in this space yet. Be the first to share!</p>
              </div>
            ) : (
              posts.map(post => <PostCard key={post.id} post={post} onUpdate={handlePostCreated} />)
            )}
          </div>
          <aside className="hidden lg:block sticky top-24 self-start space-y-4">
            <AdSlot placement="spaces_sidebar" variant="compact" />
          </aside>
        </div>
      )}
    </div>
  );
};

export default SpacePage;
